import React, { useEffect, useState, useMemo } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Search, Heart, ShoppingBag, ArrowRight, SlidersHorizontal, X, Sparkles, Tag, ChevronDown } from "lucide-react";
import ImmersiveLayout from "../components/ImmersiveLayout";
import { useLanguage } from "../contexts/LanguageContext";
import { useWishlist } from "../contexts/WishlistContext";
import { useCart } from "../contexts/CartContext";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;
const EASE = [0.22, 1, 0.36, 1];

const OCCASIONS = ["love", "gratitude", "healing", "celebration", "remembrance", "friendship", "appreciation", "valentine", "anniversary", "mother"];
const STYLES = ["romantic", "elegant", "calm", "bright", "nostalgic", "minimal", "warm"];
const SIZES = ["small", "medium", "large"];
const SORT_OPTIONS = [
  { key: "newest", label_id: "Terbaru", label_en: "Newest" },
  { key: "featured", label_id: "Pilihan Kami", label_en: "Featured" },
  { key: "price_asc", label_id: "Harga: Rendah \u2192 Tinggi", label_en: "Price: Low \u2192 High" },
  { key: "price_desc", label_id: "Harga: Tinggi \u2192 Rendah", label_en: "Price: High \u2192 Low" }
];

/**
 * E-commerce Catalog — uniform grid + hero banner + categories strip + featured rail.
 * Preserves dark immersive aesthetic but in a shoppable, marketplace-like layout.
 */
export default function CatalogPage() {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const { isWishlisted, toggleWishlist } = useWishlist();
  const { addToCart } = useCart();
  const [searchParams, setSearchParams] = useSearchParams();

  const [products, setProducts] = useState([]);
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const PAGE_SIZE = 12;
  const [search, setSearch] = useState("");
  const [activeOccasion, setActiveOccasion] = useState("all");
  const [activeStyle, setActiveStyle] = useState("all");
  const [activeSize, setActiveSize] = useState("all");
  const [priceRange, setPriceRange] = useState({ min: "", max: "" });
  const [sort, setSort] = useState("newest");
  const [sortOpen, setSortOpen] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const id = language === "id";
  const collectionId = searchParams.get("collection");

  const fmt = (p) => new Intl.NumberFormat("id-ID").format(p);

  const resolveImg = (raw) => {
    if (!raw) return "https://images.unsplash.com/photo-1551468220-0a25172193f9?w=800&q=80";
    if (raw.startsWith("http")) return raw;
    return `${BACKEND}${raw}`;
  };

  // Reset page when filters change
  useEffect(() => {
    setPage(1);
    setProducts([]);
    setHasMore(true);
  }, [search, activeOccasion, activeStyle, activeSize, priceRange.min, priceRange.max, sort, collectionId]);

  useEffect(() => {
    axios.get(`${API}/collections`).then(r => setCollections(r.data.collections || [])).catch(() => {});
    axios.get(`${API}/products?featured=true&limit=8`).then(r => setFeaturedProducts(r.data.products || [])).catch(() => {});
  }, []);

  useEffect(() => {
    const fetchProducts = async () => {
      const isFirstPage = page === 1;
      if (isFirstPage) setLoading(true); else setLoadingMore(true);
      const params = new URLSearchParams();
      if (search) params.set("search", search);
      if (activeOccasion !== "all") params.set("occasion", activeOccasion);
      if (activeStyle !== "all") params.set("style", activeStyle);
      if (activeSize !== "all") params.set("size", activeSize);
      if (priceRange.min) params.set("min_price", priceRange.min);
      if (priceRange.max) params.set("max_price", priceRange.max);
      if (collectionId) params.set("collection_id", collectionId);
      params.set("limit", String(PAGE_SIZE));
      params.set("page", String(page));
      try {
        const res = await axios.get(`${API}/products?${params.toString()}`);
        let arr = res.data.products || [];
        const total = res.data.total || arr.length;
        setTotalCount(total);
        if (sort === "price_asc") arr = [...arr].sort((a, b) => (a.price || 0) - (b.price || 0));
        if (sort === "price_desc") arr = [...arr].sort((a, b) => (b.price || 0) - (a.price || 0));
        if (sort === "featured") arr = [...arr].sort((a, b) => (b.is_featured ? 1 : 0) - (a.is_featured ? 1 : 0));
        setProducts(prev => isFirstPage ? arr : [...prev, ...arr]);
        setHasMore(arr.length === PAGE_SIZE);
      } catch {
        if (isFirstPage) setProducts([]);
      }
      if (isFirstPage) setLoading(false); else setLoadingMore(false);
    };
    fetchProducts();
  }, [search, activeOccasion, activeStyle, activeSize, priceRange, sort, collectionId, page]);

  const clearFilters = () => {
    setActiveOccasion("all");
    setActiveStyle("all");
    setActiveSize("all");
    setPriceRange({ min: "", max: "" });
    setSearch("");
    setSearchParams({});
  };

  const activeFiltersCount = useMemo(() =>
    (activeOccasion !== "all" ? 1 : 0)
    + (activeStyle !== "all" ? 1 : 0)
    + (activeSize !== "all" ? 1 : 0)
    + (priceRange.min ? 1 : 0)
    + (priceRange.max ? 1 : 0)
    + (collectionId ? 1 : 0),
    [activeOccasion, activeStyle, activeSize, priceRange, collectionId]
  );

  const handleQuickAdd = async (e, product) => {
    e.stopPropagation();
    // If product has variants, navigate to detail page to let user pick
    if (product.has_variants && product.variants?.some(v => v.status !== "inactive")) {
      navigate(`/product/${product.product_id}`);
      toast.info(id ? "Pilih varian terlebih dahulu" : "Please select a variant first");
      return;
    }
    try {
      await addToCart(product.product_id, 1, [], { openDrawer: true });
      toast.success(id ? "Ditambahkan ke keranjang" : "Added to cart");
    } catch {
      toast.error(id ? "Gagal menambahkan" : "Failed to add");
    }
  };

  const handleToggleWishlist = (e, productId) => {
    e.stopPropagation();
    toggleWishlist(productId);
  };

  const currentSort = SORT_OPTIONS.find(s => s.key === sort) || SORT_OPTIONS[0];

  return (
    <ImmersiveLayout>
      {/* ============ HERO BANNER ============ */}
      <section className="relative pt-28 pb-12 px-6 lg:px-16" data-testid="catalog-hero">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: EASE }}
            className="relative overflow-hidden rounded-3xl glass-dark p-8 lg:p-14"
          >
            {/* Ambient blur gradients */}
            <div className="absolute -top-20 -right-20 w-[440px] h-[440px] rounded-full pointer-events-none"
              style={{ background: "radial-gradient(circle, rgba(212,133,155,0.25) 0%, rgba(18,14,13,0) 70%)", filter: "blur(60px)" }}
            />
            <div className="absolute -bottom-32 -left-10 w-[360px] h-[360px] rounded-full pointer-events-none"
              style={{ background: "radial-gradient(circle, rgba(197,219,123,0.18) 0%, rgba(18,14,13,0) 70%)", filter: "blur(60px)" }}
            />
            <div className="relative z-10 grid lg:grid-cols-12 gap-10 items-end">
              <div className="lg:col-span-8">
                <div className="flex items-center gap-3 mb-4">
                  <span className="w-8 h-px bg-seikat-pink" />
                  <span className="eyebrow-immersive text-seikat-lime">{id ? "Koleksi Bouquet" : "Bouquet Catalog"}</span>
                </div>
                <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-seikat-bone leading-[0.95]" data-testid="catalog-title">
                  {id ? <>Pilih <em>rangkaian</em>, atau biarkan ia <em>memilihmu</em>.</> : <>Choose an <em>arrangement</em>, or let it <em>choose you</em>.</>}
                </h1>
                <p className="mt-5 font-body text-seikat-bone/70 max-w-xl leading-relaxed">
                  {id
                    ? "Setiap bouquet adalah baris puisi yang berbeda. Cari berdasarkan momen, gaya, atau biarkan matamu yang memilih."
                    : "Each bouquet is a different line of poetry. Search by moment, style, or simply let your eye decide."}
                </p>
              </div>
              <div className="lg:col-span-4 grid grid-cols-3 lg:grid-cols-1 gap-3 lg:gap-2">
                <Stat icon={Sparkles} value={products.length} label={id ? "Rangkaian" : "Weaves"} />
                <Stat icon={Tag} value={collections.length || 3} label={id ? "Koleksi" : "Collections"} />
                <Stat icon={Heart} value={featuredProducts.length} label={id ? "Pilihan" : "Featured"} />
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ============ CATEGORIES / COLLECTIONS STRIP ============ */}
      {collections.length > 0 && (
        <section className="px-6 lg:px-16 pb-10" data-testid="catalog-collections-strip">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-5">
              <h2 className="eyebrow-immersive">{id ? "Jelajahi Koleksi" : "Browse Collections"}</h2>
              {collectionId && (
                <button onClick={() => setSearchParams({})} className="text-[10px] tracking-widest uppercase text-seikat-pink hover:text-seikat-bone transition-colors">
                  {id ? "Tampilkan Semua" : "Show All"} <X size={10} className="inline ml-1" />
                </button>
              )}
            </div>
            <div className="flex gap-3 overflow-x-auto no-scrollbar pb-2 snap-x snap-mandatory">
              {collections.map(c => {
                const active = collectionId === c.collection_id;
                return (
                  <button
                    key={c.collection_id}
                    onClick={() => active ? setSearchParams({}) : setSearchParams({ collection: c.collection_id })}
                    data-testid={`collection-chip-${c.collection_id}`}
                    data-magnet
                    className={`relative shrink-0 snap-start w-[260px] h-[140px] rounded-2xl overflow-hidden group transition-all ${active ? "ring-2 ring-seikat-pink" : ""}`}
                  >
                    <img
                      src={c.cover_image || "https://images.unsplash.com/photo-1490750967868-88aa4486c946?w=600&q=80"}
                      alt={id ? c.name_id : c.name_en}
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-seikat-ink/85 via-seikat-ink/40 to-transparent" />
                    <div className="absolute bottom-0 left-0 right-0 p-4 text-left">
                      <p className="eyebrow-immersive text-[9px] mb-1">{id ? "Koleksi" : "Collection"}</p>
                      <p className="font-heading text-xl text-seikat-bone leading-tight">{id ? c.name_id : c.name_en}</p>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* ============ FEATURED RAIL ============ */}
      {featuredProducts.length > 0 && !collectionId && !search && (
        <section className="px-6 lg:px-16 pb-12" data-testid="catalog-featured">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-end justify-between mb-5">
              <div>
                <span className="eyebrow-immersive text-seikat-lime">{id ? "Pilihan Atelier" : "Atelier Picks"}</span>
                <h2 className="mt-2 font-heading text-3xl text-seikat-bone">
                  {id ? <>Yang sedang <em>dicintai</em> minggu ini</> : <>Most <em>loved</em> this week</>}
                </h2>
              </div>
            </div>
            <div className="flex gap-5 overflow-x-auto no-scrollbar pb-3 snap-x">
              {featuredProducts.slice(0, 8).map((p, i) => (
                <FeaturedCard key={p.product_id} product={p} index={i} navigate={navigate} resolveImg={resolveImg} id={id} fmt={fmt} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ============ STICKY SEARCH + SORT BAR ============ */}
      <section className="sticky top-0 z-30 bg-seikat-ink/90 backdrop-blur-xl border-y border-white/8 py-3 px-6 lg:px-16" data-testid="catalog-search-bar">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row gap-3 items-stretch md:items-center">
          <div className="flex-1 flex items-center gap-3 bg-white/5 border border-white/10 rounded-full px-5 py-2.5">
            <Search size={15} className="text-seikat-bone-muted" />
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder={id ? "Cari bouquet, momen, perasaan..." : "Search bouquet, moment, feeling..."}
              className="flex-1 bg-transparent outline-none text-seikat-bone placeholder:text-seikat-bone-muted/60 font-body text-sm"
              data-testid="catalog-search-input"
            />
          </div>
          <div className="flex gap-2 items-center">
            <button
              onClick={() => setFiltersOpen(true)}
              className="btn-pill text-[10px] px-4 py-2.5 relative"
              data-testid="catalog-open-filters"
              data-magnet
            >
              <SlidersHorizontal size={12} /> {id ? "Filter" : "Filters"}
              {activeFiltersCount > 0 && (
                <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-seikat-pink text-seikat-ink text-[9px] font-medium flex items-center justify-center">
                  {activeFiltersCount}
                </span>
              )}
            </button>
            <div className="relative">
              <button
                onClick={() => setSortOpen(v => !v)}
                className="btn-pill text-[10px] px-4 py-2.5"
                data-testid="catalog-sort-toggle"
                data-magnet
              >
                {id ? currentSort.label_id : currentSort.label_en} <ChevronDown size={12} />
              </button>
              <AnimatePresence>
                {sortOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: -8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                    transition={{ duration: 0.25, ease: EASE }}
                    className="absolute right-0 top-full mt-2 w-60 glass-dark-strong rounded-2xl p-2 z-40"
                    data-testid="catalog-sort-menu"
                  >
                    {SORT_OPTIONS.map(opt => (
                      <button
                        key={opt.key}
                        onClick={() => { setSort(opt.key); setSortOpen(false); }}
                        className={`w-full text-left px-3 py-2.5 rounded-lg text-xs font-body transition-colors ${sort === opt.key ? "bg-seikat-pink/15 text-seikat-pink" : "text-seikat-bone hover:bg-white/5"}`}
                        data-testid={`sort-option-${opt.key}`}
                      >
                        {id ? opt.label_id : opt.label_en}
                      </button>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </section>

      {/* ============ ACTIVE FILTERS PILLS ============ */}
      {activeFiltersCount > 0 && (
        <section className="px-6 lg:px-16 pt-5" data-testid="catalog-active-filters">
          <div className="max-w-7xl mx-auto flex flex-wrap gap-2">
            {activeOccasion !== "all" && <FilterChip label={`${id ? "Momen" : "Occasion"}: ${activeOccasion}`} onClear={() => setActiveOccasion("all")} />}
            {activeStyle !== "all" && <FilterChip label={`${id ? "Gaya" : "Style"}: ${activeStyle}`} onClear={() => setActiveStyle("all")} />}
            {activeSize !== "all" && <FilterChip label={`${id ? "Ukuran" : "Size"}: ${activeSize}`} onClear={() => setActiveSize("all")} />}
            {priceRange.min && <FilterChip label={`${id ? "Min" : "Min"} Rp ${fmt(priceRange.min)}`} onClear={() => setPriceRange(p => ({ ...p, min: "" }))} />}
            {priceRange.max && <FilterChip label={`${id ? "Maks" : "Max"} Rp ${fmt(priceRange.max)}`} onClear={() => setPriceRange(p => ({ ...p, max: "" }))} />}
            <button onClick={clearFilters} className="text-[10px] tracking-widest uppercase text-seikat-pink hover:text-seikat-bone transition-colors px-3 py-1.5" data-testid="clear-all-filters">
              {id ? "Hapus Semua" : "Clear All"} · ({activeFiltersCount})
            </button>
          </div>
        </section>
      )}

      {/* ============ PRODUCT GRID ============ */}
      <section className="relative py-12 px-6 lg:px-16" data-testid="catalog-grid-section">
        <div className="max-w-7xl mx-auto">
          {/* Result summary */}
          {!loading && (
            <div className="flex items-center justify-between mb-8">
              <p className="font-body text-sm text-seikat-bone-muted">
                <span className="text-seikat-bone">{products.length}</span> {id ? "rangkaian ditemukan" : products.length === 1 ? "weave found" : "weaves found"}
              </p>
            </div>
          )}

          {loading ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5 lg:gap-7">
              {Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="aspect-[3/4] rounded-2xl bg-white/5 animate-pulse" />
              ))}
            </div>
          ) : products.length === 0 ? (
            <div className="text-center py-32 glass-dark rounded-3xl" data-testid="catalog-empty">
              <Sparkles size={36} className="mx-auto text-seikat-pink/60 mb-6" strokeWidth={1.4} />
              <p className="font-heading text-3xl text-seikat-bone">{id ? "Belum ada bouquet yang cocok." : "No bouquets matched."}</p>
              <p className="font-body text-seikat-bone/60 text-sm mt-3 max-w-md mx-auto">{id ? "Coba ubah filter atau kata kunci lain." : "Try adjusting filters or a different keyword."}</p>
              <button onClick={clearFilters} className="btn-pill btn-pill--solid mt-8" data-testid="empty-clear">
                {id ? "Reset Filter" : "Reset Filters"}
              </button>
            </div>
          ) : (
            <motion.div
              layout
              className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5 lg:gap-7"
              data-testid="catalog-grid"
            >
              <AnimatePresence mode="popLayout">
                {products.map((p, i) => {
                  const wished = isWishlisted(p.product_id);
                  return (
                    <ProductCard
                      key={p.product_id}
                      product={p}
                      index={i}
                      id={id}
                      wished={wished}
                      navigate={navigate}
                      onWishlist={(e) => handleToggleWishlist(e, p.product_id)}
                      onQuickAdd={(e) => handleQuickAdd(e, p)}
                      resolveImg={resolveImg}
                      fmt={fmt}
                    />
                  );
                })}
              </AnimatePresence>
            </motion.div>
          )}

          {/* Load More button */}
          {!loading && hasMore && products.length > 0 && (
            <div className="flex justify-center mt-12" data-testid="load-more-section">
              <button
                onClick={() => setPage(p => p + 1)}
                disabled={loadingMore}
                data-testid="load-more-btn"
                className="btn-pill btn-pill--solid px-10 py-3"
              >
                {loadingMore
                  ? <><span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin inline-block" /> {id ? "Memuat..." : "Loading..."}</>
                  : <>{id ? "Muat Lebih Banyak" : "Load More"}</>
                }
              </button>
            </div>
          )}
          {!loading && !hasMore && products.length > PAGE_SIZE && (
            <p className="text-center font-body text-seikat-bone-muted text-sm mt-8" data-testid="end-of-catalog">
              {id ? `Semua ${products.length} rangkaian ditampilkan` : `All ${products.length} arrangements shown`}
            </p>
          )}
        </div>
      </section>

      {/* ============ PROMO BANNER (bottom) ============ */}
      <section className="px-6 lg:px-16 pb-24" data-testid="catalog-promo-banner">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, ease: EASE }}
            className="relative overflow-hidden rounded-3xl glass-dark p-10 lg:p-14 grid lg:grid-cols-2 gap-10 items-center"
          >
            <div className="absolute -bottom-32 -right-20 w-[400px] h-[400px] rounded-full pointer-events-none"
              style={{ background: "radial-gradient(circle, rgba(212,133,155,0.25) 0%, rgba(18,14,13,0) 70%)", filter: "blur(50px)" }}
            />
            <div className="relative z-10">
              <span className="eyebrow-immersive text-seikat-lime">{id ? "Belum menemukan?" : "Can't find it?"}</span>
              <h2 className="mt-3 font-heading text-4xl lg:text-5xl text-seikat-bone leading-[0.95]">
                {id ? <>Biarkan AI <em>merangkainya</em> untukmu.</> : <>Let AI <em>weave it</em> for you.</>}
              </h2>
              <p className="mt-4 font-body text-seikat-bone/65 max-w-md">
                {id
                  ? "Ceritakan momen dan perasaanmu, dan kami akan merangkai bouquet yang tepat \u2014 dengan bunga, kartu, dan halaman memori."
                  : "Tell us the moment and the feeling, and we will weave the right bouquet \u2014 with flowers, a card, and a memory page."}
              </p>
              <div className="mt-7 flex flex-wrap gap-3">
                <button onClick={() => navigate("/memory-bouquet")} className="btn-pill btn-pill--solid" data-testid="promo-memory-bouquet">
                  <Sparkles size={14} /> {id ? "Memory Bouquet" : "Memory Bouquet"} <ArrowRight size={14} />
                </button>
                <button onClick={() => navigate("/gift-card")} className="btn-pill" data-testid="promo-gift-card">
                  {id ? "Buat Kartu Hadiah" : "Design Gift Card"}
                </button>
              </div>
            </div>
            <div className="relative z-10 hidden lg:block">
              <img
                src="https://images.unsplash.com/photo-1563207153-f403bf289096?w=800&q=80"
                alt=""
                className="w-full aspect-[4/3] object-cover rounded-2xl"
              />
            </div>
          </motion.div>
        </div>
      </section>

      {/* ============ FILTER DRAWER ============ */}
      <AnimatePresence>
        {filtersOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setFiltersOpen(false)}
              className="fixed inset-0 bg-seikat-ink/70 backdrop-blur-sm z-[80]"
            />
            <motion.aside
              initial={{ x: "-100%" }}
              animate={{ x: 0 }}
              exit={{ x: "-100%" }}
              transition={{ duration: 0.5, ease: EASE }}
              className="fixed top-0 left-0 bottom-0 z-[90] w-full sm:w-[420px] flex flex-col border-r border-white/8"
              style={{ background: "#120E0D" }}
              data-testid="catalog-filters-drawer"
            >
              <header className="flex items-center justify-between px-7 py-6 border-b border-white/8">
                <div>
                  <p className="eyebrow-immersive">{id ? "Filter" : "Filters"}</p>
                  <h3 className="mt-1 font-heading text-2xl text-seikat-bone">{id ? "Persempit Pilihan" : "Refine Your Search"}</h3>
                </div>
                <button onClick={() => setFiltersOpen(false)} className="w-9 h-9 rounded-full glass-dark flex items-center justify-center text-seikat-bone hover:text-seikat-pink transition-colors" data-testid="close-filters">
                  <X size={16} />
                </button>
              </header>
              <div className="flex-1 overflow-y-auto px-7 py-6 space-y-8">
                <FilterGroup label={id ? "Momen" : "Occasion"}>
                  <FilterPills options={["all", ...OCCASIONS]} active={activeOccasion} onChange={setActiveOccasion} idPrefix="occasion" allLabel={id ? "Semua" : "All"} />
                </FilterGroup>
                <FilterGroup label={id ? "Gaya" : "Style"}>
                  <FilterPills options={["all", ...STYLES]} active={activeStyle} onChange={setActiveStyle} idPrefix="style" allLabel={id ? "Semua" : "All"} />
                </FilterGroup>
                <FilterGroup label={id ? "Ukuran" : "Size"}>
                  <FilterPills options={["all", ...SIZES]} active={activeSize} onChange={setActiveSize} idPrefix="size" allLabel={id ? "Semua" : "All"} />
                </FilterGroup>
                <FilterGroup label={id ? "Rentang Harga (Rp)" : "Price Range (Rp)"}>
                  <div className="grid grid-cols-2 gap-3">
                    <input type="number" placeholder={id ? "Min" : "Min"} value={priceRange.min} onChange={e => setPriceRange(p => ({ ...p, min: e.target.value }))} className="immersive-input" data-testid="price-min" />
                    <input type="number" placeholder={id ? "Maks" : "Max"} value={priceRange.max} onChange={e => setPriceRange(p => ({ ...p, max: e.target.value }))} className="immersive-input" data-testid="price-max" />
                  </div>
                </FilterGroup>
              </div>
              <footer className="border-t border-white/8 px-7 py-5 flex gap-3">
                <button onClick={clearFilters} className="btn-pill flex-1 justify-center" data-testid="filters-clear">
                  {id ? "Reset" : "Clear"}
                </button>
                <button onClick={() => setFiltersOpen(false)} className="btn-pill btn-pill--solid flex-1 justify-center" data-testid="filters-apply">
                  {id ? "Terapkan" : "Apply"} · {products.length}
                </button>
              </footer>
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </ImmersiveLayout>
  );
}

// ---------- Sub-components ----------
function Stat({ icon: Icon, value, label }) {
  return (
    <div className="flex items-center gap-3 px-4 py-3 rounded-2xl bg-white/[0.04] border border-white/8">
      <div className="w-9 h-9 rounded-xl bg-seikat-pink/15 flex items-center justify-center">
        <Icon size={14} className="text-seikat-pink" strokeWidth={1.6} />
      </div>
      <div>
        <div className="font-heading text-2xl text-seikat-bone leading-none">{value}</div>
        <div className="font-body text-[9px] tracking-[0.25em] uppercase text-seikat-bone-muted mt-1">{label}</div>
      </div>
    </div>
  );
}

function ProductCard({ product: p, index: i, id, wished, navigate, onWishlist, onQuickAdd, resolveImg, fmt }) {
  // Calculate price range from variants
  const activeVariants = p.variants?.filter(v => v.status !== "inactive") || [];
  const prices = activeVariants.map(v => v.price).filter(Boolean);
  const minPrice = prices.length ? Math.min(...prices) : p.price || 0;
  const maxPrice = prices.length ? Math.max(...prices) : p.price || 0;
  const hasPriceRange = prices.length > 1 && minPrice !== maxPrice;
  const displayPrice = selectedVariantPriceOrBase(p);
  const uniqueSizes = [...new Set(activeVariants.map(v => v.size).filter(Boolean))];
  const hasVariants = p.has_variants && activeVariants.length > 0;
  // Out of stock: all tracked variants have stock ≤ 0
  const allOutOfStock = hasVariants && activeVariants.every(v => v.track_stock && (v.stock || 0) <= 0);

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.95 }}
      transition={{ duration: 0.5, delay: (i % 8) * 0.04, ease: EASE }}
      className="group cursor-pointer"
      onClick={() => navigate(`/product/${p.product_id}`)}
      data-testid={`catalog-card-${p.product_id}`}
      data-magnet
    >
      <div className="relative aspect-[3/4] overflow-hidden rounded-2xl bg-white/5">
        <img
          src={resolveImg(p.images?.[0] || p.image_url)}
          alt={id ? p.name_id : p.name_en}
          className="w-full h-full object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-110"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-seikat-ink/70 via-seikat-ink/0 to-transparent opacity-80" />

        {/* Badges */}
        <div className="absolute top-3 left-3 flex flex-col gap-1">
          {p.is_featured && (
            <span className="text-[9px] tracking-widest uppercase px-2.5 py-1 rounded-full bg-seikat-lime/90 text-seikat-ink font-medium">
              {id ? "Pilihan" : "Featured"}
            </span>
          )}
          {allOutOfStock && (
            <span className="text-[9px] tracking-widest uppercase px-2.5 py-1 rounded-full bg-black/60 text-white/80" data-testid={`sold-out-${p.product_id}`}>
              {id ? "Habis" : "Sold Out"}
            </span>
          )}
        </div>

        {/* Wishlist */}
        <button
          onClick={onWishlist}
          className="absolute top-3 right-3 w-9 h-9 rounded-full glass-dark flex items-center justify-center text-seikat-bone hover:text-seikat-pink transition-colors"
          aria-label="Wishlist"
          data-testid={`catalog-wishlist-${p.product_id}`}
          data-magnet
        >
          <Heart size={13} fill={wished ? "#D4859B" : "none"} color={wished ? "#D4859B" : "currentColor"} />
        </button>

        {/* Quick add overlay */}
        <div className="absolute inset-x-3 bottom-3 opacity-0 translate-y-3 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-500">
          <button
            onClick={onQuickAdd}
            className="w-full btn-pill btn-pill--solid text-[10px] py-2.5"
            data-testid={`catalog-quick-add-${p.product_id}`}
          >
            <ShoppingBag size={12} />
            {hasVariants ? (id ? "Pilih Varian" : "Choose Variant") : (id ? "Tambah Cepat" : "Quick Add")}
          </button>
        </div>
      </div>
      <div className="pt-4 pb-2">
        <p className="eyebrow-immersive text-[9px] mb-1.5">{p.occasion_tags?.[0] || p.occasion?.[0] || p.style_tags?.[0] || p.style || "Signature"}</p>
        <h3 className="font-heading text-lg sm:text-xl text-seikat-bone leading-tight line-clamp-1 group-hover:text-seikat-pink transition-colors">
          {id ? p.name_id : p.name_en}
        </h3>
        {/* Price: show range if variants have different prices */}
        <p className="font-body text-sm text-seikat-pink mt-1" data-testid={`catalog-price-${p.product_id}`}>
          {hasPriceRange
            ? `Rp ${fmt(minPrice)} – ${fmt(maxPrice)}`
            : `Rp ${fmt(minPrice || p.price || 0)}`}
        </p>
        {/* Size chips */}
        {uniqueSizes.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-1.5" data-testid={`catalog-sizes-${p.product_id}`}>
            {uniqueSizes.slice(0, 4).map(s => (
              <span key={s} className="text-[8px] tracking-widest uppercase px-2 py-0.5 rounded-full border border-white/15 text-seikat-bone-muted">{s}</span>
            ))}
            {uniqueSizes.length > 4 && <span className="text-[8px] text-seikat-bone-muted/60">+{uniqueSizes.length - 4}</span>}
          </div>
        )}
      </div>
    </motion.div>
  );
}

function selectedVariantPriceOrBase(p) {
  if (!p.has_variants || !p.variants?.length) return p.price || 0;
  const def = p.variants.find(v => v.is_default && v.status !== "inactive") || p.variants.find(v => v.status !== "inactive");
  return def?.price ?? p.price ?? 0;
}

function FeaturedCard({ product: p, index: i, navigate, resolveImg, id, fmt }) {
  return (
    <motion.div
      initial={{ opacity: 0, x: 24 }}
      whileInView={{ opacity: 1, x: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.6, delay: i * 0.05, ease: EASE }}
      onClick={() => navigate(`/product/${p.product_id}`)}
      className="snap-start shrink-0 w-[240px] cursor-pointer group"
      data-testid={`featured-${p.product_id}`}
      data-magnet
    >
      <div className="relative aspect-[4/5] overflow-hidden rounded-2xl">
        <img
          src={resolveImg(p.images?.[0] || p.image_url)}
          alt={id ? p.name_id : p.name_en}
          className="w-full h-full object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-110"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-seikat-ink/85 via-transparent to-transparent" />
        <div className="absolute inset-x-0 bottom-0 p-4">
          <p className="font-heading text-lg text-seikat-bone leading-tight line-clamp-1">{id ? p.name_id : p.name_en}</p>
          <p className="font-body text-xs text-seikat-pink mt-0.5">Rp {fmt(p.price || 0)}</p>
        </div>
      </div>
    </motion.div>
  );
}

function FilterChip({ label, onClear }) {
  return (
    <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-seikat-pink/10 border border-seikat-pink/30 text-[10px] tracking-widest uppercase text-seikat-pink">
      {label}
      <button onClick={onClear} className="hover:text-seikat-bone transition-colors">
        <X size={10} />
      </button>
    </span>
  );
}

function FilterGroup({ label, children }) {
  return (
    <div>
      <p className="eyebrow-immersive mb-3">{label}</p>
      {children}
    </div>
  );
}

function FilterPills({ options, active, onChange, idPrefix, allLabel }) {
  return (
    <div className="flex flex-wrap gap-2">
      {options.map(opt => {
        const isActive = active === opt;
        return (
          <button
            key={opt}
            onClick={() => onChange(opt)}
            data-testid={`filter-${idPrefix}-${opt}`}
            className={`px-3 py-1.5 rounded-full text-[10px] tracking-widest uppercase capitalize transition-all ${isActive ? "bg-seikat-pink text-seikat-ink" : "border border-white/10 text-seikat-bone-muted hover:text-seikat-bone hover:border-white/25"}`}
          >
            {opt === "all" ? allLabel : opt.replace(/_/g, " ")}
          </button>
        );
      })}
    </div>
  );
}
